import Heading from 'LuxKit/Typography/Heading'
import { useMemo } from 'react'
import * as React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import qs from 'qs'
import type { TourV2TrendingDestination } from './TourV2TrendingLocationsList'
import styled from 'styled-components'
import TrendingDestinationsTile from 'home/components/TrendingDestinations/TrendingDestinationTile'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import FullWidthCarousel from 'LuxKit/Carousel/FullWidthCarousel'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Group from 'components/utils/Group'

const GridContainer = styled(FullWidthCarousel)`
  > div {
    grid-template-rows: 1fr 1fr 1fr;

    ${mediaQueryUp.tablet} {
      grid-template-rows: unset;
    }
  }
`

const GridContainerCarousel = styled(CardCarousel)`
  > div > div {
    grid-template-rows: 1fr 1fr 1fr;

    ${mediaQueryUp.tablet} {
      grid-template-rows: unset;
    }
  }
`

interface Props {
  locations: Array<TourV2TrendingDestination>
  fullWidthOverflow?: boolean
  title?: React.ReactNode
}

function TourV2TrendingDestinationsCarousel(props: Props) {
  const { locations, fullWidthOverflow = true, title } = props
  const intl = useIntl()

  const localizedNames = useMemo<Record<string, string>>(() => ({
    Vietnam: intl.formatMessage({
      id: 'W6-1elW9708nzJ0orJcQ',
      defaultMessage: 'Vietnam',
      description: 'Vietnam shown as a trending destination label in the tours carousel',
    }),
    Japan: intl.formatMessage({
      id: '06LonCF6ZnxSMCreVrz1',
      defaultMessage: 'Japan',
      description: 'Japan shown as a trending destination label in the tours carousel',
    }),
    Australia: intl.formatMessage({
      id: 'hWuJIhuoG0x0lcS0kBTR',
      defaultMessage: 'Australia',
      description: 'Australia shown as a trending destination label in the tours carousel',
    }),
    'Sri Lanka': intl.formatMessage({
      id: 'iR3HVq1Wr9a6jD4ZZt15',
      defaultMessage: 'Sri Lanka',
      description: 'Sri Lanka shown as a trending destination label in the tours carouselrousel',
    }),
    Spain: intl.formatMessage({
      id: 'uBtAtuf9az4XCsI53nmy',
      defaultMessage: 'Spain',
      description: 'Spain shown as a trending destination label in the tours carousel',
    }),
    Africa: intl.formatMessage({
      id: 'O2sZSD7l707eNyXQXrYW',
      defaultMessage: 'Africa',
      description: 'Africa shown as a trending destination label in the tours carousel',
    }),
    Egypt: intl.formatMessage({
      id: 'rkDYXncEahVbOnKy7KIS',
      defaultMessage: 'Egypt',
      description: 'Egypt shown as a trending destination label in the tours carousel',
    }),
    India: intl.formatMessage({
      id: '4O2XjTf63MdbmsY7Ztuc',
      defaultMessage: 'India',
      description: 'India shown as a trending destination label in the tours carousel',
    }),
    Turkey: intl.formatMessage({
      id: 'DrjOeoJ_dzncVOM1kvRh',
      defaultMessage: 'Turkey',
      description: 'Turkey shown as a trending destination label in the tours carousel',
    }),
  }), [intl])

  const trendingDestinations = useMemo(() => {
    return locations.map((location) => ({
      name: localizedNames[location.destinationName] ?? location.destinationName,
      imageId: location.imageId,
      destinationId: location.placeId,
      url: `/search/tours?${qs.stringify({
          destinationId: location.placeId,
          destinationName: location.destinationName,
        })}`,
    }))
  }, [locations, localizedNames])

  const defaultTitle = (
    <FormattedMessage
      id="xAzTT7yDe0Lv8y8amGCL"
      defaultMessage="Immerse yourself in trending <i>destinations</i>"
      description="Title for trending destinations section with italic 'destinations'"
      values={{
        i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
      }}
    />
  )

  return <Group direction="vertical" gap={32}>
    {fullWidthOverflow && <>
      <LayoutContainer>
        <Heading variant="heading2">
          {title ?? defaultTitle}
        </Heading>
      </LayoutContainer>
      <GridContainer snap="start" gap={12}>
        {
          trendingDestinations.map((trendingDestination, index) => {
            return (
              <TrendingDestinationsTile
                key={trendingDestination.name}
                trendingDestination={trendingDestination}
                index={index}
              />
            )
          })
        }
      </GridContainer>
    </>}
    {!fullWidthOverflow && <>
      <Heading variant="heading2">
        {title ?? defaultTitle}
      </Heading>
      <GridContainerCarousel gap={12} arrowsOnlyOnHover>
        {trendingDestinations.map((trendingDestination, index) => {
          return (
            <TrendingDestinationsTile
              key={trendingDestination.name}
              trendingDestination={trendingDestination}
              index={index}
            />
          )
        })}
      </GridContainerCarousel>
    </>}
  </Group>
}

export default TourV2TrendingDestinationsCarousel
